import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined'
import CreateNewFolderOutlined from '@mui/icons-material/CreateNewFolderOutlined'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

interface FileRepositoryToolbarProps {
  canCreate: boolean
  search: string
  onSearchChange: (value: string) => void
  sortBy: 'name' | 'created_at' | 'sizeInBytes'
  sortOrder: 'asc' | 'desc'
  onSortByChange: (value: 'name' | 'created_at' | 'sizeInBytes') => void
  onSortOrderChange: (value: 'asc' | 'desc') => void
  onUploadClick: () => void
  onNewFolderClick: () => void
}

export function FileRepositoryToolbar({
  canCreate,
  search,
  onSearchChange,
  sortBy,
  sortOrder,
  onSortByChange,
  onSortOrderChange,
  onUploadClick,
  onNewFolderClick,
}: FileRepositoryToolbarProps) {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState(search)

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchChange(searchValue)
    }
  }

  const handleSortByChange = (event: SelectChangeEvent<string>) => {
    onSortByChange(event.target.value as 'name' | 'created_at' | 'sizeInBytes')
  }

  const toggleSortOrder = () => {
    onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: '16px',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {/* Left: action buttons */}
      <Box sx={{ display: 'flex', gap: '12px' }}>
        {canCreate && (
          <>
            <Button
              variant="outlined"
              onClick={onUploadClick}
              startIcon={<CloudUploadOutlined />}
              sx={{
                height: '36px',
                minWidth: '300px',
                ':hover': {
                  backgroundColor: '#FF5E1E',
                  color: '#fff',
                },
              }}
            >
              {t('fileRepository.button.upload')}
            </Button>
            <Button
              variant="outlined"
              onClick={onNewFolderClick}
              startIcon={<CreateNewFolderOutlined />}
              sx={{
                height: '36px',
                minWidth: '300px',
                ':hover': {
                  backgroundColor: '#FF5E1E',
                  color: '#fff',
                },
              }}
            >
              {t('fileRepository.button.newFolder')}
            </Button>
          </>
        )}
      </Box>

      {/* Right: search + sort */}
      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('fileRepository.search.placeholder')}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          onBlur={() => onSearchChange(searchValue)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(0,0,0,0.4)' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: '250px' }}
        />
        <Select
          value={sortBy}
          onChange={handleSortByChange}
          size="small"
          sx={{ minWidth: '140px' }}
        >
          <MenuItem value="name">{t('fileRepository.sort.name')}</MenuItem>
          <MenuItem value="created_at">{t('fileRepository.sort.created_at')}</MenuItem>
          <MenuItem value="sizeInBytes">{t('fileRepository.sort.sizeInBytes')}</MenuItem>
        </Select>
        <Tooltip title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
          <IconButton onClick={toggleSortOrder} size="small">
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}
